import { DocumentSubtype, ProductType } from 'constants/product'
import { Nullable } from 'models/helpers'
import { ProductClassificationFlags } from 'models/product'

/** Every DocumentSubtype member is name-identical to the legacy document ProductType it replaces. */
const documentSubtypeProductTypes: Record<DocumentSubtype, ProductType> = {
  [DocumentSubtype.MUNICIPAL]: ProductType.MUNICIPAL,
  [DocumentSubtype.LAW_INQUIRIES]: ProductType.LAW_INQUIRIES,
  [DocumentSubtype.LAND_REGISTRATION]: ProductType.LAND_REGISTRATION,
  [DocumentSubtype.DOCUMENTATION]: ProductType.DOCUMENTATION,
  [DocumentSubtype.PROTECTION]: ProductType.PROTECTION,
  [DocumentSubtype.CONSTRUCTION_FILE]: ProductType.CONSTRUCTION_FILE,
}

/**
 * Derives the legacy ProductType shown on the creative-instructions surfaces (banner "Product type"
 * label + instruction line) from the backend-computed classification flags and document subtype,
 * replacing the wire `productType` the backend is retiring (ISSUE-3263 / ISSUE-3265).
 *
 * Mirrors backend `ProductClassificationFlags.deriveLegacyTypeBucket` one-to-one, including
 * document-subtype-first and STAGING_360 before FLOOR_PLAN. Deliberately SEPARATE from
 * `deriveProductTypeBucket`: that 4-flag helper feeds gallery/deal-card decisions whose callers must
 * keep resolving staging-360 and document products to their existing buckets. Do not merge the two.
 *
 * Certificate primaries collapse to PHOTO and floor-plan bundles read FLOOR_PLAN (the backend's
 * isFloorPlan ORs in the two floor-plan bundle types), matching the backend; neither has instruction
 * templates either way. The Matterport-with-visit VIRTUAL_VISIT answer rides the backend's
 * legacy-type shim (the classifier alone would not raise isVirtualVisit for it) and flips to
 * FLOOR_PLAN when the legacy type column drops — an expected, backend-owned change.
 *
 * A document subtype the frontend enum does not know yet falls through to the flag tail instead of
 * returning undefined, so the surface stays populated until the enum catches up.
 */
export const deriveCreativeInstructionsProductType = (
  flags: ProductClassificationFlags,
  documentSubtype: Nullable<DocumentSubtype>
): ProductType => {
  const documentType = documentSubtype ? (documentSubtypeProductTypes[documentSubtype] as ProductType | undefined) : undefined
  if (documentType) return documentType
  if (flags.isAiPhotoEditing) return ProductType.AI_PHOTO_EDITING
  if (flags.isVideo) return ProductType.VIDEO
  if (flags.isVirtualVisit) return ProductType.VIRTUAL_VISIT
  if (flags.isStaging360) return ProductType.STAGING_360
  if (flags.isFloorPlan) return ProductType.FLOOR_PLAN
  return ProductType.PHOTO
}
